import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { cn } from "@/shared/lib";

import { useAssetLookup } from "../../api/useAssetLookup";
import { formatList } from "../../format/formatList";
import { formatPercent } from "../../format/formatPercent";
import type { AnalysisDeath } from "../../model/Analysis";
import type { Finding } from "../../model/Finding";
import { findingsOf } from "../../model/findingsOf";
import { FindingFixes } from "../../findings/FindingFixes";
import { KillerCounter } from "../../findings/KillerCounter";
import { groupDeathsByKiller } from "../../model/groupDeathsByKiller";
import { SEVERITY_LABELS } from "../../model/SEVERITY_LABELS";
import { AssetIcon } from "../AssetIcon";

const DEATHS = msg({
  id: "analysis.deaths.count",
  message: "{count, plural, one {# death} other {# deaths}}",
});
const DAMAGE_SHARE = msg({
  id: "analysis.deaths.damage_share",
  message: "{share} of hero damage to you",
});
const TIME_TO_KILL = msg({ id: "analysis.deaths.time_to_kill", message: "{times} s to die" });
const NOT_HERO = msg({ id: "analysis.deaths.not_hero", message: "Creeps and towers" });

type KillerCardsProps = {
  deaths: readonly AnalysisDeath[];
  findings: readonly Finding[];
  // Только убийцы с находкой `killed_by_hero`: остальные видны в списке смертей.
  onlyRepeat?: boolean;
};

// Убийцы игрока карточками: герой, сколько раз убил и чем выделился. Герой с находкой
// `killed_by_hero` подсвечен и показывает долю урона и совет, как ему противостоять,
// остальные — за сколько секунд убили.
export const KillerCards = ({ deaths, findings, onlyRepeat = false }: KillerCardsProps) => {
  const { _, i18n } = useLingui();
  const findHero = useAssetLookup("heroes");
  const repeatKillers = findingsOf(findings, "killed_by_hero");
  return (
    <ul className="flex flex-wrap gap-3">
      {groupDeathsByKiller(deaths).map(({ heroId, deaths: byKiller }) => {
        const finding = repeatKillers.find((f) => f.params.hero_id === heroId);
        if (onlyRepeat && finding === undefined) {
          return null;
        }
        const hero = heroId === null ? null : findHero(heroId);
        const note =
          finding !== undefined
            ? _({
                ...DAMAGE_SHARE,
                values: { share: formatPercent(finding.params.damage_share, i18n.locale) },
              })
            : _({
                ...TIME_TO_KILL,
                values: {
                  times: formatList(
                    byKiller.map((death) => String(Math.round(death.time_to_kill_s))),
                    i18n.locale,
                  ),
                },
              });
        return (
          <li
            key={heroId ?? "not-hero"}
            data-killer={heroId ?? "none"}
            {...(finding !== undefined && {
              "data-finding": finding.code,
              "data-severity": finding.severity,
            })}
            className={cn(
              "flex items-center gap-3 rounded-[10px] border px-3.5 py-2.5",
              finding !== undefined ? "border-bad/40 bg-bad/10" : "border-border bg-surface-raised",
            )}
          >
            {hero === null ? (
              <span
                aria-hidden="true"
                className="inline-flex size-[52px] shrink-0 items-center justify-center rounded-[10px] bg-surface text-2xl text-subtle"
              >
                ×
              </span>
            ) : (
              <AssetIcon asset={hero} size={52} decorative className="rounded-[10px]" />
            )}
            <div className="flex flex-col">
              <span className="font-semibold">
                {finding !== undefined && (
                  <span className="sr-only">{_(SEVERITY_LABELS[finding.severity])}: </span>
                )}
                {hero === null ? _(NOT_HERO) : hero.name}
              </span>
              <span className="font-mono text-xl font-semibold text-bad">
                {_({ ...DEATHS, values: { count: byKiller.length } })}
              </span>
              {heroId !== null && <span className="text-xs text-muted">{note}</span>}
              {finding !== undefined && (
                <KillerCounter
                  finding={finding}
                  className="max-w-md text-sm text-foreground-soft"
                />
              )}
              {finding !== undefined && (
                <FindingFixes
                  finding={finding}
                  dataOnly
                  className="mt-1 flex max-w-md flex-col gap-1"
                />
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};
